import { useDispatch } from "react-redux";
import { UnknownAction } from "redux";
import { SHARED_REDUCER_ACTIONS } from "../store/reducers/shared.reducer";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes.enum";
import { sitePermissions } from "utils/helpers/enums/permissions.enum";

interface Permission {
  action: string;
  resource: string;
  superAdmin: boolean;
}

interface UserData {
  permissions: Permission[];
  roles: string[];
  [key: string]: any;
}

const useStore = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const dispatchAction = (type: string, payload?: any): void => {
    const dispatcher: UnknownAction = { type, payload };
    dispatch(dispatcher);
  };

  const setLoading = (isLoading: boolean): void => {
    dispatchAction(SHARED_REDUCER_ACTIONS.LOADING, isLoading);
  };

  const setToken = (payload: null | string): void => {
    dispatch({ type: SHARED_REDUCER_ACTIONS.SAVE_TOKEN, payload });
    if (payload) localStorage.setItem("token", payload);
  };

  const userData = (payload: any): void => {
    dispatchAction(SHARED_REDUCER_ACTIONS.SAVE_USER_DATA, payload);
    localStorage.setItem("userData", JSON.stringify(payload));
  };

  const getUser = () => {
    const userData = localStorage.getItem("userData");
    if (userData) {
      return JSON.parse(userData);
    } else {
      return null;
    }
  };

  const getToken = (): string | null => {
    return localStorage.getItem("token");
  };

  const setRole = (role: any): void => {
    localStorage.setItem("role", JSON.stringify(role));
  };

  const getPermissions = (): any => {
    const user = localStorage.getItem("userData");
    if (user) {
      const userData = JSON.parse(user);
      return userData.permissions || null;
    }
    return null;
  };

  const logout = () => {
    dispatchAction(SHARED_REDUCER_ACTIONS.LOGOUT);
    localStorage.clear();
    navigate(siteRoutes.login);
  };

  const setExpandSidebar = (isExpanded: boolean): void => {
    dispatchAction(SHARED_REDUCER_ACTIONS.SET_EXPAND_SIDEBAR, isExpanded);
  };

  const getAllUserData = (): UserData | null => {
    const stored = localStorage.getItem("userData");
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object") return null;

    return parsed as UserData;
  };

  const hasPermission = (permission: sitePermissions): boolean => {
    const user = getAllUserData();
    if (!user) return false;

    if (user.currentRole?.includes("SUPER_ADMIN")) return true;

    return (
      user.permissions?.some((perm) => perm.action === permission) ?? false
    );
  };

  const hasResourceAccess = (resource: string): boolean => {
    const user = getAllUserData();
    if (!user) return false;

    if (user.currentRole?.includes("SUPER_ADMIN")) return true;

    return (
      user.permissions?.some((perm) => perm.resource === resource) ?? false
    );
  };

  return {
    setToken,
    setLoading,
    dispatchAction,
    logout,
    getToken,
    getPermissions,
    setRole,
    getUser,
    setExpandSidebar,
    userData,
    hasPermission,
    hasResourceAccess,
  };
};

export default useStore;
